import { SCHEMA_VERSION, type BrowserState } from "./state.js";

/**
 * Single-database, single-store IndexedDB layout for ZenTrack browser data.
 *
 *   database: zentrack-web
 *   version:  1
 *   store:    state
 *   key:      main
 *
 * The whole BrowserState object is stored under one key. This keeps the runtime
 * simple, transactional, and easy to back up as a single JSON blob. A more
 * normalized schema would be possible later — see the plan's known follow-ups.
 */
export const DB_NAME = "zentrack-web";
export const DB_VERSION = 1;
export const STORE_NAME = "state";
export const STATE_KEY = "main";

type IndexedDBFactory = typeof indexedDB;

/**
 * Resolve the global indexedDB. Indirected so tests can inject fake-indexeddb
 * without depending on jsdom globals.
 */
function getIDB(): IndexedDBFactory | undefined {
  if (typeof indexedDB !== "undefined") {
    return indexedDB;
  }

  // Some test environments (older Node) lack the global; the caller is
  // expected to stub it via fake-indexeddb in that case.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).indexedDB;
}

export interface OpenDbOptions {
  idb?: IndexedDBFactory;
  /** Override the database name (defaults to "zentrack-web"). Mainly for tests. */
  dbName?: string;
}

/** Open (and upgrade if needed) the ZenTrack web database. */
export function openDb(options: OpenDbOptions = {}): Promise<IDBDatabase> {
  const idb = options.idb ?? getIDB();

  if (!idb) {
    throw new Error("IndexedDB is not available in this environment.");
  }

  const dbName = options.dbName ?? DB_NAME;

  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = idb.open(dbName, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Failed to open the ZenTrack database."));
    request.onblocked = () =>
      reject(new Error("The ZenTrack database is blocked by another tab."));
  });
}

/** Read the persisted state, or null when nothing has been stored yet. */
export async function readPersistedState(
  db: IDBDatabase,
  key: string = STATE_KEY,
): Promise<BrowserState | null> {
  return new Promise<BrowserState | null>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = () => {
      resolve((request.result as BrowserState | undefined) ?? null);
    };
    request.onerror = () =>
      reject(request.error ?? new Error("Failed to read local state."));
  });
}

/** Overwrite the persisted state under the given key. */
export function writePersistedState(
  db: IDBDatabase,
  state: BrowserState,
  key: string = STATE_KEY,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    store.put(state, key);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Failed to write local state."));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("Local state write was aborted."));
  });
}

/** Remove the persisted state (used by "clear local data" flows). */
export function clearPersistedState(
  db: IDBDatabase,
  key: string = STATE_KEY,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    store.delete(key);

    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Failed to clear local state."));
  });
}

export { SCHEMA_VERSION };
